import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, CheckCircle, DollarSign, Users, Calendar, Shield, ArrowLeft, Building2, Clock, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import Navigation from '../components/Navigation';
import { useAuth, API } from '../App';
import { toast } from 'sonner';

const BecomeProvider = () => {
  const navigate = useNavigate();
  const { user, hasRole, login, setActiveRole } = useAuth();
  const [loading, setLoading] = useState(false);

  // If user already has vendor role, redirect to vendor dashboard
  React.useEffect(() => {
    if (hasRole('vendor')) {
      navigate('/vendor/dashboard');
    }
  }, [hasRole, navigate]);

  const benefits = [
    {
      icon: Users,
      title: 'Reach Local Customers',
      description: 'Get discovered by customers in your area actively looking for event services.'
    },
    {
      icon: Calendar,
      title: 'Manage Your Schedule',
      description: 'Accept bookings and manage your calendar all in one place.'
    },
    {
      icon: DollarSign,
      title: 'Secure Payments',
      description: 'Get paid securely through Stripe with automatic deposits to your bank account.'
    },
    {
      icon: Shield,
      title: 'Build Your Reputation',
      description: 'Earn reviews and badges to stand out from the competition.'
    }
  ];

  const requirements = [
    'A service you want to offer (photography, DJ, catering, etc.)',
    'Basic contact information and service area',
    'Photos of your work (recommended but not required to start)'
  ];

  const handleStartOnboarding = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Call the become-vendor endpoint to add vendor role
      const response = await axios.post(
        `${API}/users/become-vendor`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update the token with the new one that includes vendor role
      const newToken = response.data.token;
      if (newToken) {
        localStorage.setItem('token', newToken);
      }
      
      // Update the user in local state with new roles
      if (response.data.user) {
        login(response.data.user, newToken || token);
      }
      
      // Explicitly set active role to vendor after becoming a provider (silent - we show custom toast below)
      setActiveRole('vendor', { silent: true });
      
      toast.success('Welcome! Let\'s set up your provider profile.');
      
      // Navigate to vendor dashboard to complete profile
      navigate('/vendor/dashboard?tab=profile');
    } catch (error) {
      console.error('Error becoming vendor:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already')) {
        // User already has vendor role, just redirect
        navigate('/vendor/dashboard');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to start provider setup. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen-safe bg-gray-50">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Become a Service Provider
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Turn your skills into income. Join thousands of providers offering event services on Celey.
          </p>
        </div>

        {/* Reassurance Banner - No LLC Required */}
        <Card className="mb-8 bg-emerald-50 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                <Building2 className="h-6 w-6 text-emerald-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-emerald-800 mb-2">No Business License or LLC Required</h3>
                <p className="text-sm text-emerald-700 mb-3">
                  You can start as an individual — no business formation needed. During Stripe setup, 
                  simply select <span className="font-medium">"Individual / Sole Proprietor"</span> and use your personal information.
                </p>
                <div className="flex flex-wrap gap-4 text-xs text-emerald-600">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    No LLC needed
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    No EIN required
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" />
                    Use your SSN
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Benefits Grid */}
        <div className="grid sm:grid-cols-2 gap-6 mb-12">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <Card key={index} className="border-0 shadow-sm">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Icon className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                      <p className="text-sm text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Requirements Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>What You'll Need</CardTitle>
            <CardDescription>
              Just the basics to get started — you can add more details later
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {requirements.map((req, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{req}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Stripe Info - More reassuring */}
        <Card className="mb-8 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Secure Payments via Stripe</h3>
                <p className="text-sm text-gray-600 mb-2">
                  We use Stripe to handle all payments securely. You can set this up later, 
                  but you'll need it before accepting paid bookings.
                </p>
                <p className="text-xs text-blue-600">
                  <span className="font-medium">Celey never sees your bank details</span> — Stripe handles everything securely.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="text-center p-4 bg-white rounded-lg border">
            <Clock className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">5 min</p>
            <p className="text-xs text-gray-500">To get started</p>
          </div>
          <div className="text-center p-4 bg-white rounded-lg border">
            <DollarSign className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">$0</p>
            <p className="text-xs text-gray-500">To sign up</p>
          </div>
          <div className="text-center p-4 bg-white rounded-lg border">
            <Star className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">5%</p>
            <p className="text-xs text-gray-500">Only when paid</p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <Button
            size="lg"
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
            onClick={handleStartOnboarding}
            disabled={loading}
          >
            {loading ? 'Setting up...' : 'Start Provider Setup'}
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
          <p className="text-sm text-gray-500 mt-4">
            You can complete your profile at your own pace
          </p>
        </div>

        {/* Already logged in notice */}
        {user && (
          <div className="mt-8 p-4 bg-gray-100 rounded-lg text-center">
            <p className="text-sm text-gray-600">
              Logged in as <span className="font-medium">{user.email}</span>
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Your provider profile will be linked to this account
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BecomeProvider;
